import { useEffect, useState } from "react";
import axios from "axios";
import { useMutation, useQueryClient } from "react-query";
import TTaxGroup from "../types/TTaxGroup";
import Tab from "components/tab";
import TTax from "../types/TTax";
import { IoClose } from "react-icons/io5";

type TProps = {
  groupName: string;
  setGroupName: (groupName: string) => void;
  taxes: any[];
  taxGroups: TTaxGroup[];
  selectedTaxes: any[];
  setSelectedTaxes: (taxes: any[]) => void;
  handleTaxSelection: (tax: TTax, isCombinedTaxRate?: boolean, selectedTab?: string) => void;
  handleSaveGroup: () => void;
  isEditMode: boolean;
  setIsGroupModalOpen: (isOpen: boolean) => void;
  selectedGroup: TTaxGroup | null;
  setSelectedGroup: (group: TTaxGroup | null) => void;
  setSelectedCombinedTaxRateId: (id: number) => void;
};

const GroupModal = ({
  groupName,
  setGroupName,
  taxes,
  taxGroups,
  selectedTaxes,
  setSelectedTaxes,
  handleTaxSelection,
  handleSaveGroup,
  isEditMode,
  setIsGroupModalOpen,
  selectedGroup,
  setSelectedGroup,
  setSelectedCombinedTaxRateId,
}: TProps) => {
  const [selectedTab, setSelectedTab] = useState("discount");
  const [localTaxes, setLocalTaxes] = useState<any[]>([]);
	const [error, setError] = useState({ name: false, tax: false });
  const [isCombinedTaxRate, setIsCombinedTaxRate] = useState(false);

  const activeGroups = taxGroups.filter((group) => group.archived === false);

  const queryClient = useQueryClient();

  const archiveTaxGroup = useMutation(
    async (group: { id: number; archived: boolean }) => {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL}/api/v1/company/tax_groups/archive/${group.id}`,
        { archived: !group.archived } // Toggle archive state
      );

      return { id: group.id, archived: !group.archived };
    },
    {
      onMutate: async (updatedGroup) => {
        await queryClient.cancelQueries("taxes");

        // Snapshot current data
        const previousGroups = queryClient.getQueryData<TTaxGroup[]>("taxes");

        if (!Array.isArray(previousGroups)) {
          console.warn(
            "previousGroups is not an array. Skipping optimistic update."
          );
          return { previousGroups };
        }

        // Optimistically update UI
        queryClient.setQueryData<TTaxGroup[]>("taxes", (oldGroups = []) =>
          oldGroups.map((group) =>
            group.id === updatedGroup.id
              ? { ...group, archived: !updatedGroup.archived }
              : group
          )
        );

        return { previousGroups };
      },
      onSuccess: (updatedGroup) => {
        if (selectedGroup && selectedGroup.id === updatedGroup.id) {
          setSelectedGroup({
            ...selectedGroup,
            archived: updatedGroup.archived,
          });
        }

        queryClient.invalidateQueries("taxes");
      },
      onError: (err, newGroup, context) => {
        console.error("Error archiving tax group:", err);
        if (context?.previousGroups) {
          queryClient.setQueryData("taxes", context.previousGroups);
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries("taxes");
      },
    }
  );

  const filterArchivedTaxes = isEditMode
    ? taxes
    : taxes.filter((tax) => tax.archived === false);

  useEffect(() => {
    if (isCombinedTaxRate) {
      setSelectedTab("tax");
      setLocalTaxes(
        filterArchivedTaxes.filter((tax) => tax.tax_type === "tax")
      );
    } else {
      setSelectedTab("discount");
      setLocalTaxes(
        filterArchivedTaxes.filter((tax) => tax.tax_type === "discount")
      );
    }
  }, [isCombinedTaxRate, taxes, isEditMode]);

  // reset selected taxes when combined tax rate is changed
  useEffect(() => {
    setSelectedTaxes([]);
  }, [isCombinedTaxRate]);

  // load in tax groups if edit mode and selected group
  useEffect(() => {
    if (isEditMode && selectedGroup && selectedGroup.taxes) {
      const reconstructedTaxes: any[] = [];
      const processedTaxIds = new Set();
      
      // Get all tax-type taxes from selectedGroup
      const taxTypeTaxes = selectedGroup.taxes.filter(tax => tax.tax_type === "tax");
      const taxTypeIds = taxTypeTaxes.map(tax => tax.id);

      let combinedId = 0; 
      
      // Find exact matching tax groups for tax-type taxes only
      if (taxTypeTaxes.length > 0) {
        const matchingGroup = taxGroups.find(group => 
          group.taxes && 
          group.taxes.length > 0 &&
          group.taxes.length === taxTypeTaxes.length &&
          group.taxes.every(groupTax => taxTypeIds.includes(groupTax.id)) &&
          taxTypeIds.every(taxId => group.taxes.some(groupTax => groupTax.id === taxId))
        );
        
        if (matchingGroup) {
          // Add the matching group and mark all its tax IDs as processed
          reconstructedTaxes.push(matchingGroup);
          matchingGroup.taxes.forEach(groupTax => processedTaxIds.add(groupTax.id));
          combinedId = Number(matchingGroup.id);
        }
      }
      
      // Add remaining individual taxes (non-tax types and unmatched tax types)
      selectedGroup.taxes.forEach(tax => {
        if (!processedTaxIds.has(tax.id)) {
          reconstructedTaxes.push(tax);
        }
      });
      
      setSelectedTaxes(reconstructedTaxes);
      setSelectedCombinedTaxRateId(combinedId);
    }
  }, [isEditMode, selectedGroup, taxGroups]);

  const tabData = {
    discount: { tab: "Discounts" },
    fee: { tab: "Fees" },
    tax: { tab: "Taxes" },
    tax_group: { tab: "Tax Groups" },
  };

  const tabDataGroup = {
    tax: { tab: "Taxes" },
    // tax_group: { tab: "Tax Groups" },
  }

  const handleTabClick = (key: string) => {
    switch (key) {
      case "discount":
        setSelectedTab("discount");
        setLocalTaxes(
          filterArchivedTaxes.filter((tax) => tax.tax_type === "discount")
        );
        break;
      case "fee":
        setSelectedTab("fee");
        setLocalTaxes(
          filterArchivedTaxes.filter((tax) => tax.tax_type === "fee")
        );
        break;
      case "tax":
        setSelectedTab("tax");
        setLocalTaxes(
          filterArchivedTaxes.filter((tax) => tax.tax_type === "tax")
        );
        break;
      case "tax_group":
        setSelectedTab("tax_group");
        // Only show tax groups that are combined tax rates (contain only tax_type === "tax" items)
        const combinedTaxRateGroups = activeGroups.filter(group => 
          group.taxes && 
          group.taxes.length > 0 && 
          group.taxes.every(tax => tax.tax_type === "tax")
        );
        setLocalTaxes(combinedTaxRateGroups);
        break;
      default:
        break;
    }
  };

	const checkIfComplete = () => {
		setError({
			name: groupName.trim() === "" ? true : false,
			tax: selectedTaxes.length === 0 ? true : false,
		})
	};

  const maxTax = selectedTaxes.filter(t => t.tax_type === "tax" || t.taxes).length;

  // Sort selected taxes by category: discounts first, then fees, then tax/combined tax rate
  const sortedSelectedTaxes = [...selectedTaxes].sort((a, b) => {
    const categoryOrder: { [key: string]: number } = { discount: 1, fee: 2, tax: 3 };
    const aCategory = a.tax_type || (a.taxes ? 'tax' : 'tax');
    const bCategory = b.tax_type || (b.taxes ? 'tax' : 'tax');
    
    if (categoryOrder[aCategory] !== categoryOrder[bCategory]) {
      return categoryOrder[aCategory] - categoryOrder[bCategory];
    }
    
    // If same category, maintain current order
    return selectedTaxes.indexOf(a) - selectedTaxes.indexOf(b);
  });

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-[750px] min-w-[500px]">
        {/* Selection */}
				<div className="flex space-x-4">
					<div className="w-1/2">
						<h3 className="text-lg font-semibold mb-3">
							{isEditMode ? "Edit Tax Group" : "Create New Tax Group"}
						</h3>
            <label className="block mb-2">
              <span className="text-gray-700 font-medium">Rate Type</span>
              <select
                name="list_type"
                value={isCombinedTaxRate ? "combined_tax_rate" : "group"}
                onChange={(e) => setIsCombinedTaxRate(e.target.value === "combined_tax_rate")}
                className="w-full p-2 border border-gray-300 rounded"
              >
                <option value="group">Group</option>
                <option value="combined_tax_rate">Combined Tax Rate</option>
              </select>
            </label>
						<input
							type="text"
							value={groupName}
							onChange={(e) => setGroupName(e.target.value)}
							placeholder="Group Name"
							className={`w-full mb-1 p-2 border rounded ${error.name ? "border-red-500" : "border-gray-300"}`}
						/>
						<div>
              {isCombinedTaxRate ? (
                <Tab
                  data={tabDataGroup}
                  onClick={handleTabClick}
                  defaultTab="tax"
                />
              ) : (
                <Tab
                  data={tabData}
                  onClick={handleTabClick}
                  defaultTab="discount"
                />
              )}

							<div className="space-y-2 max-h-[354px] overflow-y-auto mt-4 pr-2">
								{localTaxes.map((tax) => (
                  <>
                    <label
                      key={tax.id}
                      className="flex items-center justify-between hover:bg-gray-100"
                    >
                      <div className="flex items-center space-x-3 hover:cursor-pointer">
                        {(selectedTab === "tax" || selectedTab === "tax_group") && !isCombinedTaxRate ? (
                          <input
                            type="radio"
                            checked={selectedTaxes.some((t) => String(t.id) === String(tax.id))}
                            onChange={() => handleTaxSelection(tax, false, selectedTab)}
                            className="form-checkbox h-5 w-5 text-blue-600"
                          />
                        ) : (
                          <input
                            type="checkbox"
                            checked={selectedTaxes.some((t) => String(t.id) === String(tax.id))}
                            onChange={() => handleTaxSelection(tax, isCombinedTaxRate)}
                            className="form-checkbox h-5 w-5 text-blue-600"
                          />
                        )}

                        <div>{tax.label}</div>
                      </div>
                      {selectedTab !== "tax_group" && (
                        <div className="text-sm text-gray-500">
                          {tax.list_type === "percent"
                            ? `${parseFloat(tax.list_rate).toString()}%`
                            : `$${parseFloat(tax.list_rate).toFixed(2)}`}
                        </div>
                      )}
                    </label>
                    {selectedTab === "tax_group" && (
                      <div className="text-sm text-gray-500 ml-8">
                        {tax.taxes && tax.taxes.map((t: TTax) => {
                          return (
                            <div key={t.id} className="flex justify-between">
                              <span>{t.label}</span>
                              <span>{t.list_type === "percent"
                                ? `${parseFloat(String(t.list_rate))}%`
                                : `$${parseFloat(String(t.list_rate)).toFixed(2)}`}</span>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </>
								))}
							</div>
						</div>
					</div>

					{/* Display */}
					<div className="w-1/2">
						<div className="text-lg font-semibold border-b pb-2">
							Selected Taxes
						</div>
            {!isCombinedTaxRate && (
              <div className="my-2 text-xs text-gray-500">
                Selected Tax / Tax Group ({maxTax}/1)
              </div>
            )}
						<div className={`space-y-2 overflow-y-auto mt-2 border rounded 
              ${error.tax ? "border-red-500" : "border-gray-300"}
              ${isCombinedTaxRate ? "max-h-[524px] h-[524px]" : "max-h-[500px] h-[500px] "}
            `}>
							{sortedSelectedTaxes.map((tax) => (
								<div key={tax.id} className={`p-2 bg-gray-50 rounded m-2 
                  ${maxTax === 1 && 
                    (tax.tax_type === "tax" || tax.taxes) && "border border-primary"}
                `}>
									<div className="flex items-start justify-between">
										<div>{tax.label}</div>
										<button onClick={() => handleTaxSelection(tax)}>
											<IoClose className="text-red-500 hover:cursor-pointer" size={20} />
										</button>
									</div>
									<div className="flex items-center justify-between">
										<div className="text-sm text-gray-500 capitalize">{tax.tax_type}</div>
                    {!tax.taxes && (
                      <div className="text-sm text-gray-500">
                        {tax.list_type === "percent"
                          ? `${parseFloat(String(tax.list_rate))}%`
                          : `$${parseFloat(String(tax.list_rate)).toFixed(2)}`}
                      </div>
                    )}
									</div>
                  {/* Combined Tax Rate */}
                  {tax.taxes && (
                    <div className="text-sm text-gray-500">
                      {tax.taxes.map((t: TTax) => {
                        return (
                          <div key={t.id} className="flex justify-between">
                            <span>{t.label}f</span>
                            <span>{t.list_type === "percent"
                              ? `${parseFloat(String(t.list_rate))}%`
                              : `$${parseFloat(String(t.list_rate)).toFixed(2)}`}</span>
                          </div>
                        )
                      })}
                    </div>
                  )}
								</div>
							))}
						</div>
					</div>
				</div>

        {/* Buttons */}
        <div className="flex justify-end space-x-4 mt-5">
          <button
            onClick={() => setIsGroupModalOpen(false)}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cancel
          </button>

          {/* Archive Button - Only show if editing an existing group */}
          {isEditMode && selectedGroup && (
            <button
              onClick={() =>
                archiveTaxGroup.mutate({
                  id: selectedGroup.id!,
                  archived: selectedGroup.archived,
                })
              }
              className={`px-4 py-2 rounded transition ${
                selectedGroup.archived
                  ? "bg-green-500 hover:bg-green-600"
                  : "bg-red-500 hover:bg-red-600"
              } text-white`}
              disabled={archiveTaxGroup.isLoading}
            >
              {archiveTaxGroup.isLoading
                ? "Updating..."
                : selectedGroup.archived
                ? "Unarchive"
                : "Archive"}
            </button>
          )}

          <button
            onClick={() => {
							checkIfComplete();
							handleSaveGroup();
						}}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
          >
            {isEditMode ? "Update Group" : "Create Group"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GroupModal;
